import { useState, useEffect, useRef } from "react";
import { api, useAuth } from "@/App";
import { 
  FileText, Plus, Trash2, Upload, StickyNote, 
  File, Download, ShoppingCart
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";
import { fr } from "date-fns/locale";

export default function VehicleInternalDossier({ vehicleId }) {
  const { user } = useAuth();
  const [notes, setNotes] = useState([]);
  const [docs, setDocs] = useState([]);
  const [newNote, setNewNote] = useState("");
  const [addingNote, setAddingNote] = useState(false);
  const [showNoteForm, setShowNoteForm] = useState(false);
  const [showDocForm, setShowDocForm] = useState(false);
  const [docType, setDocType] = useState("document");
  const [docLabel, setDocLabel] = useState("");
  const [docMontant, setDocMontant] = useState("");
  const [docFournisseur, setDocFournisseur] = useState("");
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const isAdmin = user?.role === "admin" || user?.specialite === "secretaire";

  const fetchData = async () => {
    if (!isAdmin) return;
    try {
      const [notesRes, docsRes] = await Promise.all([
        api.get(`/vehicles/${vehicleId}/internal-notes`),
        api.get(`/vehicles/${vehicleId}/internal-docs`)
      ]);
      setNotes(notesRes.data);
      setDocs(docsRes.data);
    } catch (err) {
      console.error("Échec chargement du dossier interne:", err);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicleId]);

  if (!isAdmin) return null;

  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    setAddingNote(true);
    try {
      await api.post(`/vehicles/${vehicleId}/internal-notes`, {
        vehicle_id: vehicleId,
        content: newNote.trim()
      });
      setNewNote("");
      setShowNoteForm(false);
      toast.success("Note ajoutée");
      fetchData();
    } catch (err) {
      toast.error("Erreur lors de l'ajout");
    }
    setAddingNote(false);
  };

  const handleDeleteNote = async (noteId) => {
    try {
      await api.delete(`/vehicles/${vehicleId}/internal-notes/${noteId}`);
      toast.success("Note supprimée");
      fetchData();
    } catch (err) {
      toast.error("Erreur");
    }
  };

  const handleUploadDoc = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const reader = new FileReader();
      reader.onloadend = async () => {
        const base64 = reader.result;
        await api.post(`/vehicles/${vehicleId}/internal-docs`, {
          vehicle_id: vehicleId,
          filename: file.name,
          file_data: base64,
          doc_type: docType,
          label: docLabel || file.name,
          montant: docMontant ? parseFloat(docMontant) : null,
          fournisseur: docFournisseur || ""
        });
        toast.success(docMontant 
          ? `Document ajouté (${docMontant}€ ajouté au coût de revient)` 
          : "Document ajouté");
        setShowDocForm(false);
        setDocLabel("");
        setDocMontant("");
        setDocFournisseur("");
        setDocType("document");
        fetchData();
      };
      reader.readAsDataURL(file);
    } catch (err) {
      toast.error("Erreur lors de l'upload");
    }
    if (fileInputRef.current) fileInputRef.current.value = "";
    setUploading(false);
  };

  const handleDeleteDoc = async (docId) => {
    try {
      await api.delete(`/vehicles/${vehicleId}/internal-docs/${docId}`);
      toast.success("Document supprimé");
      fetchData();
    } catch (err) {
      toast.error("Erreur");
    }
  };

  const handleDownloadDoc = (doc) => {
    const link = document.createElement("a");
    link.href = doc.file_data;
    link.download = doc.filename;
    link.click();
  };

  const docTypeLabels = {
    document: "Document",
    bon_commande: "Bon de commande",
    photo: "Photo",
    facture: "Facture fournisseur",
    autre: "Autre"
  };

  return (
    <Card className="border-slate-200" data-testid="internal-dossier-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-lg flex items-center gap-2">
          <FileText className="h-5 w-5 text-indigo-600" />
          Dossier interne
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* NOTES INTERNES */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-1.5">
              <StickyNote className="h-4 w-4 text-amber-500" />
              Notes internes
            </h3>
            <Button
              variant="ghost"
              size="sm"
              className="text-indigo-600 hover:text-indigo-700 h-7 text-xs"
              onClick={() => setShowNoteForm(!showNoteForm)}
              data-testid="add-internal-note-btn"
            >
              <Plus className="h-3.5 w-3.5 mr-1" />
              Ajouter
            </Button>
          </div>

          {showNoteForm && (
            <div className="mb-3 space-y-2 p-3 bg-indigo-50 rounded-lg border border-indigo-100">
              <Textarea
                placeholder="Saisir une note interne..."
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
                className="min-h-[80px] bg-white"
                autoFocus
                data-testid="internal-note-input"
              />
              <div className="flex gap-2 justify-end">
                <Button variant="outline" size="sm" onClick={() => { setShowNoteForm(false); setNewNote(""); }}>
                  Annuler
                </Button>
                <Button
                  size="sm"
                  onClick={handleAddNote}
                  disabled={addingNote || !newNote.trim()}
                  className="bg-indigo-600 hover:bg-indigo-700"
                  data-testid="save-internal-note-btn"
                >
                  {addingNote ? <div className="spinner w-4 h-4" /> : "Enregistrer"}
                </Button>
              </div>
            </div>
          )}

          {notes.length === 0 ? (
            <p className="text-xs text-slate-400 italic">Aucune note interne</p>
          ) : (
            <div className="space-y-2">
              {notes.map((note) => (
                <div key={note.id} className="flex items-start gap-2 p-2 bg-amber-50/50 rounded border border-amber-100" data-testid={`internal-note-${note.id}`}>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-slate-700 whitespace-pre-wrap">{note.content}</p>
                    <p className="text-[10px] text-slate-400 mt-1">
                      {note.author} — {note.created_at ? format(parseISO(note.created_at), "d MMM yyyy HH:mm", { locale: fr }) : ""}
                    </p>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6 text-red-400 hover:text-red-600 hover:bg-red-50 flex-shrink-0"
                    onClick={() => handleDeleteNote(note.id)}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>

        <Separator />

        {/* DOCUMENTS INTERNES */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-1.5">
              <File className="h-4 w-4 text-blue-500" />
              Documents
            </h3>
            <Button
              variant="ghost"
              size="sm"
              className="text-indigo-600 hover:text-indigo-700 h-7 text-xs"
              onClick={() => setShowDocForm(!showDocForm)}
              data-testid="add-internal-doc-btn"
            >
              <Upload className="h-3.5 w-3.5 mr-1" />
              Ajouter
            </Button>
          </div>

          {showDocForm && (
            <div className="mb-3 space-y-3 p-3 bg-blue-50 rounded-lg border border-blue-100">
              <div>
                <Label className="text-xs font-medium">Type de document</Label>
                <Select value={docType} onValueChange={setDocType}>
                  <SelectTrigger className="mt-1 bg-white" data-testid="doc-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="document">Document</SelectItem>
                    <SelectItem value="bon_commande">Bon de commande</SelectItem>
                    <SelectItem value="facture">Facture fournisseur</SelectItem>
                    <SelectItem value="photo">Photo</SelectItem>
                    <SelectItem value="autre">Autre</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-xs font-medium">Libellé</Label>
                <Input
                  placeholder="Ex: Bon de commande pièces..."
                  value={docLabel}
                  onChange={(e) => setDocLabel(e.target.value)}
                  className="mt-1 bg-white"
                  data-testid="doc-label-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs font-medium">Montant (€)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    placeholder="0.00"
                    value={docMontant}
                    onChange={(e) => setDocMontant(e.target.value)}
                    className="mt-1 bg-white"
                    data-testid="doc-montant-input"
                  />
                  <p className="text-[10px] text-blue-600 mt-0.5">Sera ajouté au coût de revient</p>
                </div>
                <div>
                  <Label className="text-xs font-medium">Fournisseur</Label>
                  <Input
                    placeholder="Nom du fournisseur"
                    value={docFournisseur}
                    onChange={(e) => setDocFournisseur(e.target.value)}
                    className="mt-1 bg-white"
                    data-testid="doc-fournisseur-input"
                  />
                </div>
              </div>

              <div>
                <input
                  ref={fileInputRef}
                  type="file"
                  onChange={handleUploadDoc}
                  className="hidden"
                  accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx"
                />
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    className="flex-1"
                    data-testid="upload-doc-btn"
                  >
                    <Upload className="h-4 w-4 mr-2" />
                    {uploading ? "Upload en cours..." : "Choisir un fichier"}
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => { setShowDocForm(false); setDocLabel(""); setDocMontant(""); setDocFournisseur(""); }}>
                    Annuler
                  </Button>
                </div>
              </div>
            </div>
          )}

          {docs.length === 0 ? (
            <p className="text-xs text-slate-400 italic">Aucun document</p>
          ) : (
            <div className="space-y-2">
              {docs.map((doc) => (
                <div key={doc.id} className="flex items-center gap-2 p-2 bg-slate-50 rounded border border-slate-100" data-testid={`internal-doc-${doc.id}`}>
                  <div className="flex-shrink-0">
                    {doc.doc_type === "bon_commande" ? (
                      <ShoppingCart className="h-4 w-4 text-blue-500" />
                    ) : (
                      <File className="h-4 w-4 text-slate-400" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-700 truncate">{doc.label || doc.filename}</p>
                    <div className="flex items-center gap-2 text-[10px] text-slate-400">
                      <Badge variant="outline" className="text-[10px] px-1 py-0">
                        {docTypeLabels[doc.doc_type] || doc.doc_type}
                      </Badge>
                      {doc.montant > 0 && (
                        <span className="text-blue-600 font-bold">{doc.montant}€</span>
                      )}
                      {doc.fournisseur && (
                        <span>{doc.fournisseur}</span>
                      )}
                      <span>{doc.uploaded_by}</span>
                      <span>{doc.created_at ? format(parseISO(doc.created_at), "d MMM", { locale: fr }) : ""}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6 text-blue-400 hover:text-blue-600"
                      onClick={() => handleDownloadDoc(doc)}
                      title="Télécharger"
                    >
                      <Download className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6 text-red-400 hover:text-red-600 hover:bg-red-50"
                      onClick={() => handleDeleteDoc(doc.id)}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
